import { Box, Stack } from '@mui/material'
import dynamic from 'next/dynamic'
import { FC, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  ItemThumbnailType,
  MagazinePageDirection,
  MagazinePageType,
  ResourceFragment,
} from '@/clients/utoniq-core/schema'
import {
  Loading,
  PointChargeDrawer,
  PointChargeDrawerProps,
} from '@/components/organisms'
import { ItemReversibleThumbnail } from '@/components/organisms/item/ItemReversableThumbnail'
import { DRMMagazinePlayer } from '@/components/organisms/resourcePlayer/MagazinePlayer/DRMMagazinePlayer'
import { ThumbnailActionAreaCell } from '@/components/pages/OwnedItemDetail/template/components/ThumbnailActionArea'
import { config } from '@/config'
import { CellFrame, Organism } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'
import { handleChunkLoadError } from '@/lib/errorHandler'

import type { Action, State } from '../../../viewModel'
import { ItemDescriptionCell } from '../ItemDescription'
import { SupplementLinkSetCell } from '../SupplementLinkSet'

export type Props = {
  loading: boolean
  portfolio: State['portfolio']
  isFlipped: boolean
  onReverseThumbnail: () => void
  onExpand: () => void
  onSetOpenMagazineModal: (val: boolean) => void
  onClickProtectUserItem: () => void
  onViewCollectionMagazine: Action['onViewCollectionMagazine']
  onOpenFullScreen?: () => void
  onOpenMultipleImage?: () => void
}

export const OwnedItemDetailMagazineView: Organism<Props> = ({
  loading,
  portfolio,
  isFlipped,
  onClickProtectUserItem,
  onReverseThumbnail,
  onSetOpenMagazineModal,
  onViewCollectionMagazine,
  onOpenFullScreen,
  onOpenMultipleImage,
}) => {
  return (
    <CellFrame
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
      }}
    >
      <Stack
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <Stack
          alignItems="center"
          sx={{
            position: 'relative',
            '.MuiButtonBase-root': {
              height: 'fit-content',
            },
          }}
        >
          <ThumbnailActionAreaCell
            onOpenFullScreen={onOpenFullScreen}
            onOpenMultipleImage={onOpenMultipleImage}
            numberImage={portfolio?.item.images?.length ?? 0}
          />
          <ItemReversibleThumbnail
            loading={loading}
            flipProps={{
              isFlipped,
            }}
            btnPlay={{
              type: 'back',
              onClick: () => {
                onSetOpenMagazineModal(true)

                if (onViewCollectionMagazine) {
                  onViewCollectionMagazine()
                }
              },
            }}
            imageAreaProps={{
              thumbnail: portfolio?.item?.thumbnail,
              size:
                portfolio?.item.thumbnailType === ItemThumbnailType.Square
                  ? 'x-large-square'
                  : 'x-large',
              onClick: onReverseThumbnail,
            }}
          />
        </Stack>
        <ItemDescriptionCell portfolio={portfolio} />
        <Box sx={{ mt: 4, mb: 2 }}>
          {isFlipped ? (
            <IconButton.Rotate
              size="small"
              onClick={onReverseThumbnail}
              bgcolor="primary.main"
              color="white"
            />
          ) : (
            <IconButton.MagazineOn
              size="small"
              onClick={onReverseThumbnail}
              bgcolor="primary.main"
              color="white"
            />
          )}
        </Box>
      </Stack>

      <SupplementLinkSetCell
        sku={portfolio?.item.sku}
        itemId={portfolio?.item.id}
        commentCount={portfolio?.item.commentCount}
        relatedMissions={portfolio?.item.relatedMissions}
        artistId={portfolio?.item.issuedArtist?.id ?? ''}
        isUserProtected={portfolio?.isUserProtected}
        issuableNft={!!portfolio?.item.nft?.chains}
        onClickProtectUserItem={onClickProtectUserItem}
      />
    </CellFrame>
  )
}

type MagazinePlayerModalProps = {
  open: boolean
  loading: boolean
  purchasingItem: boolean
  onClose: () => void
  point?: number
  portfolio: State['portfolio']
  onBuyItem: Action['onBuyItem']
  onClickShare: () => Promise<void>
  chargeLoading?: PointChargeDrawerProps['chargeLoading']
  onPurchaseCharge: PointChargeDrawerProps['onPurchase']
  purchasablePrepaidPoints?: PointChargeDrawerProps['purchasablePrepaidPoints']
  paymentSource: PointChargeDrawerProps['paymentSource']
  /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
  enablePointCarrierPayment: boolean
}
export const MagazinePlayerModal: FC<MagazinePlayerModalProps> = ({
  open,
  loading,
  point,
  onClose,
  portfolio,
  purchasingItem,
  onBuyItem,
  onClickShare,
  chargeLoading,
  onPurchaseCharge,
  purchasablePrepaidPoints,
  paymentSource,
  enablePointCarrierPayment,
}) => {
  const [openPointCharge, setOpenPointCharge] = useState<boolean>(false)
  const isDRM = portfolio?.item?.resource?.drm?.encryptingStatus === 'encrypted'
  const { analytics } = useAnalytics()

  if (!portfolio?.item || loading)
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <Loading />
      </Box>
    )

  return (
    <>
      {isDRM ? (
        <DRMMagazinePlayer
          open={open}
          onClose={onClose}
          resource={portfolio?.item?.resource as ResourceFragment}
        />
      ) : portfolio?.item?.metadata?.magazine?.pageType ===
        MagazinePageType.Image ? (
        <MangaPlayer
          loading={loading}
          purchasingItem={purchasingItem}
          open={open}
          onClose={() => {
            onClose()
            setOpenPointCharge(false)
          }}
          item={portfolio?.item}
          point={point}
          onOpenPointCharge={() => setOpenPointCharge(true)}
          direction={
            portfolio?.item?.metadata?.magazine?.pageDirection ||
            MagazinePageDirection.Left
          }
          onBuyItem={onBuyItem}
          onClickShare={() => {
            analytics?.track('click_share', {
              applicationKey: config.applicationKey,
              artistId: portfolio?.item.issuedArtist?.id,
              itemId: portfolio?.item.id,
              name: 'magazineViewer',
            })
            onClickShare().then(() => {
              analytics?.track('click_shareMethod', {
                applicationKey: config.applicationKey,
                artistId: portfolio?.item.issuedArtist?.id,
                itemId: portfolio?.item.id,
                name: 'magazineViewer',
              })
            })
          }}
        />
      ) : (
        <MagazinePlayer
          loading={loading}
          purchasingItem={purchasingItem}
          open={open}
          onClose={() => {
            onClose()
            setOpenPointCharge(false)
          }}
          item={portfolio?.item}
          point={point}
          onOpenPointCharge={() => setOpenPointCharge(true)}
          resourceUrl={portfolio?.item?.resource?.uri || ''}
          direction={
            portfolio?.item?.metadata?.magazine?.pageDirection ||
            MagazinePageDirection.Left
          }
          onBuyItem={onBuyItem}
          onClickShare={() => {
            analytics?.track('click_share', {
              applicationKey: config.applicationKey,
              artistId: portfolio?.item.issuedArtist?.id,
              itemId: portfolio?.item.id,
              name: 'magazineViewer',
            })
            onClickShare().then(() => {
              analytics?.track('click_shareMethod', {
                applicationKey: config.applicationKey,
                artistId: portfolio?.item.issuedArtist?.id,
                itemId: portfolio?.item.id,
                name: 'magazineViewer',
              })
            })
          }}
        />
      )}
      <PointChargeDrawer
        point={point ?? 0}
        onPurchase={onPurchaseCharge}
        open={openPointCharge}
        loading={loading}
        chargeLoading={!!chargeLoading}
        purchasablePrepaidPoints={purchasablePrepaidPoints ?? null}
        onClose={() => setOpenPointCharge(false)}
        paymentSource={paymentSource}
        enablePointCarrierPayment={enablePointCarrierPayment}
        pageName="ownedItemDetail"
      />
    </>
  )
}

const MagazinePlayer = dynamic(
  () =>
    import('@/components/organisms/resourcePlayer/MagazinePlayer')
      .then(md => md.MagazinePlayer)
      .catch(async error => {
        await handleChunkLoadError(error)
        return () => null
      }),
  { ssr: false }
)

const MangaPlayer = dynamic(
  () =>
    import('@/components/organisms/resourcePlayer/MagazinePlayer/MangaPlayer')
      .then(md => md.MangaPlayer)
      .catch(async error => {
        await handleChunkLoadError(error)
        return () => null
      }),
  { ssr: false }
)
